import { useEffect, useReducer, useRef } from 'react';
import { INITIAL, press, type CalculatorKey } from '../../utils/calculator.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button } from '../../ui/primitives.tsx';

/**
 * The on-screen calculator.
 *
 * Every rule about what the keys do lives in `utils/calculator.ts`; this file is the keypad and
 * nothing else. It is a non-modal panel rather than a dialog: the point of a calculator is to use
 * it while reading the question, so it must not take the question off the screen or trap focus
 * away from it.
 *
 * It also accepts the number keys, because anyone who reaches for a calculator reaches for the
 * keyboard next, and clicking sixteen buttons to add two numbers is its own kind of insult.
 */
export function Calculator({ onClose }: { readonly onClose: () => void }) {
  const { t } = useI18n();
  const [state, dispatch] = useReducer(press, INITIAL);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const key = keyFor(event.key);
      if (key === null) {
        return;
      }
      // Only once the calculator has the focus, or typing an answer into a text question would be
      // doing arithmetic instead.
      if (panelRef.current?.contains(document.activeElement) !== true) {
        return;
      }
      event.preventDefault();
      dispatch(key);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const keypad: readonly (readonly [string, CalculatorKey])[] = [
    ['C', { kind: 'clear' }],
    ['CE', { kind: 'clear-entry' }],
    ['%', { kind: 'percent' }],
    ['÷', { kind: 'operator', value: '/' }],
    ['7', { kind: 'digit', value: '7' }],
    ['8', { kind: 'digit', value: '8' }],
    ['9', { kind: 'digit', value: '9' }],
    ['×', { kind: 'operator', value: '*' }],
    ['4', { kind: 'digit', value: '4' }],
    ['5', { kind: 'digit', value: '5' }],
    ['6', { kind: 'digit', value: '6' }],
    ['−', { kind: 'operator', value: '-' }],
    ['1', { kind: 'digit', value: '1' }],
    ['2', { kind: 'digit', value: '2' }],
    ['3', { kind: 'digit', value: '3' }],
    ['+', { kind: 'operator', value: '+' }],
    ['±', { kind: 'negate' }],
    ['0', { kind: 'digit', value: '0' }],
    ['.', { kind: 'decimal' }],
    ['=', { kind: 'equals' }],
  ];

  return (
    <div
      className="calculator"
      role="group"
      aria-label={t('calculator.title')}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="calculator__bar">
        <span className="calculator__name">{t('calculator.title')}</span>
        <Button variant="quiet" onClick={onClose}>
          {t('calculator.close')}
        </Button>
      </div>

      {/* A live region rather than an input: the value is announced when it changes, and it cannot
          be typed into directly, so there is nothing to parse and nothing to sanitise. */}
      <output className="calculator__display" aria-live="polite">
        {state.error ? t('calculator.error') : state.display}
      </output>

      <div className="calculator__keys">
        {keypad.map(([label, key]) => (
          <button
            key={label}
            type="button"
            className={`calculator__key${key.kind === 'equals' ? ' calculator__key--equals' : ''}`}
            onClick={() => {
              dispatch(key);
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The keyboard, mapped onto the same keys the buttons press. */
function keyFor(pressed: string): CalculatorKey | null {
  if (/^[0-9]$/.test(pressed)) {
    return { kind: 'digit', value: pressed };
  }
  switch (pressed) {
    case '.':
    case ',':
      return { kind: 'decimal' };
    case '+':
    case '-':
    case '*':
    case '/':
      return { kind: 'operator', value: pressed };
    case '=':
    case 'Enter':
      return { kind: 'equals' };
    case 'Backspace':
      return { kind: 'backspace' };
    case 'Escape':
      return { kind: 'clear' };
    case '%':
      return { kind: 'percent' };
    default:
      return null;
  }
}
